import { useAuth } from "@clerk/clerk-react";
import { createContext, useContext, useState } from "react";
import {
  BentoBrand,
  BentoContact,
  NewBentoContact,
  PreviewMessage,
  SavedBrandStatus,
} from "schemas/dashboard";

import { fetcherAuth } from "utils/api";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { getNow } from "utils/time";
import { useCollection } from "utils/useCollection";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";
import { previewToDraft } from "./helpers/QuickSendHelpers";

export type DraftRef = {
  bentoBrand: BentoBrand;
  emails: PreviewMessage[];
  previewContact: BentoContact | NewBentoContact;
  isAutogenerated?: boolean;
};
interface QuickSendEmailDraftsContextInterface {
  createEmailDraft: (sanitizedData: DraftRef) => void;
  createDraftLoading: number;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  createEmailDraft: defaultContextMissingFunction,
  createDraftLoading: -1,
};

const QuickSendEmailDraftsContext =
  createContext<QuickSendEmailDraftsContextInterface>(defaultInterface);

interface QuickSendEmailDraftsProviderProps {
  children: React.ReactNode;
}

const QuickSendEmailDraftsProvider = ({
  children,
}: QuickSendEmailDraftsProviderProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setAlert } = useContext(AlertContext);
  const { addDraftToCollection, addToCollectionTab, removeFromCollectionTab } =
    useCollection();

  const [createDraftLoading, setCreateDraftLoading] = useState(-1);

  const createEmailDraft = async (sanitizedData: DraftRef) => {
    const { emails, previewContact, bentoBrand } = sanitizedData;
    const bentoBrandId = Number(bentoBrand?.id);

    if (!previewContact.email) {
      return;
    }

    setCreateDraftLoading(bentoBrandId);
    addDraftToCollection(sanitizedData.bentoBrand);

    const emailDrafts = emails.map((email) => previewToDraft(email));
    const isAdmin = Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-brands/${bentoBrand?.id}/outreach-draft`,
        "POST",
        {},
        {
          previewContact,
          emailDrafts,
          isAdmin,
        },
      );
      // If a brand has an outreach contact, add it to Sent tab
      if (res.outreachDraft.bentoBrand?.orgHasOutreachContact) {
        const savedBrand = {
          bentoBrandId: bentoBrand?.id,
          bentoBrand: res.outreachDraft.bentoBrand,
          createdAt: getNow(),
        };
        removeFromCollectionTab(savedBrand, 0, SavedBrandStatus.unsent);
        addToCollectionTab(savedBrand, 0, SavedBrandStatus.sent);
      }
      setAlert(
        `Autosaved your outreach for ${bentoBrand?.normalizedName}`,
        "info",
      );
    } catch (error) {
      // Do nothing for now
    } finally {
      setCreateDraftLoading(-1);
    }
  };

  return (
    <QuickSendEmailDraftsContext.Provider
      value={{
        createEmailDraft,
        createDraftLoading,
      }}
    >
      {children}
    </QuickSendEmailDraftsContext.Provider>
  );
};

export { QuickSendEmailDraftsProvider, QuickSendEmailDraftsContext };
